import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, useInView, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Truck, Plane, Package, Shield, Clock, Award, 
  MapPin, Phone, Mail, ChevronRight, Menu, X,
  Globe, Target, Eye, Zap, Users, Building2, Lock
} from "lucide-react";
import axios from "axios";
import MapSection from "./components/MapSection";
import TrackingSection from "./components/TrackingSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo provided by user
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/xbf6wj4z_Logo%20CDR%20File.png";
const COMPANY_LOGO_FULL = "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/ng1vv19a_SKT%20New%20Logo.png";

// Images from design guidelines
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/ecce9726-c1e7-4315-b61e-55387375fbbc/images/ed4d4d90a4ff4062e5242e0d47273fe375988e03a580d6d9756f78abbf88f68e.png";
const INFRASTRUCTURE_BG = "https://static.prod-images.emergentagent.com/jobs/ecce9726-c1e7-4315-b61e-55387375fbbc/images/4c2fd6a0279cd6d9bf452c9b902ca63ac278cc3e4d8e1aa686c6d464e0f2eeb2.png";

// Rotating hero background images (3-4 dynamically changing)
const heroBackgrounds = [
  { url: HERO_BG, alt: "Highway truck operations" },
  { url: "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=2000&q=80", alt: "Cargo aircraft" },
  { url: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=2000&q=80", alt: "Container port" },
  { url: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=2000&q=80", alt: "Long chassis truck" },
];

const galleryImages = [
  { url: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1600&q=80", alt: "Long Chassis Trucks", span: "col-span-2 row-span-2", category: "Long Chassis Trucks" },
  { url: "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=1200&q=80", alt: "Air Cargo Loading", span: "col-span-1 row-span-1", category: "Air Cargo" },
  { url: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1200&q=80", alt: "Container Shipping", span: "col-span-1 row-span-2", category: "Shipping Operations" },
  { url: "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85", alt: "Highway Truck Fleet", span: "col-span-2 row-span-1", category: "Trucks" },
  { url: "https://images.unsplash.com/photo-1759272548470-d0686d071036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBjYXJnbyUyMGNvbnRhaW5lciUyMHBvcnQlMjBvcGVyYXRpb25zfGVufDB8fHx8MTc3NDY5MjYxMHww&ixlib=rb-4.1.0&q=85", alt: "Bulk Cargo Movement", span: "col-span-2 row-span-1", category: "Bulk Movements" },
  { url: "https://images.pexels.com/photos/20882743/pexels-photo-20882743.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Fleet Depot", span: "col-span-1 row-span-1", category: "Fleet Management" },
  { url: "https://images.pexels.com/photos/30536324/pexels-photo-30536324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Warehouse Operations", span: "col-span-1 row-span-1", category: "Warehousing" },
];

// Client data with brand-accurate styling
const clients = [
  { name: "L & T Infra", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/7rzmz4mg_L%26T.jpeg" },
  { name: "Robert Bosch Limited", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/wh9znhge_Bosch%20Logo.jpg" },
  { name: "Dynamatic Technologies", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/6catqyql_Dynamatic-Technologies%20Logo.jpg" },
  { name: "Brakes India", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/zsfd9gfw_Brakes%20India%20Logo.jfif" },
  { name: "Bosch Rexroth", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/3xv7t105_Rexroth%20logo_1280x720.jpg" },
  { name: "DB Schenker", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/ovpkmqpq_DB%20Schenker%20Logo.webp" },
  { name: "DHL Limited", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/6hadodiv_DHL%20Logo.jpg" },
  { name: "Igarashi Motors India", logoUrl: "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/9tviapcx_IGARASHI-logo.jpg" }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

// Section wrapper with animation
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#overview", label: "Overview" },
    { href: "#services", label: "Services" },
    { href: "#tracking", label: "Track" },
    { href: "#network", label: "Network" },
    { href: "#locations", label: "Locations" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "backdrop-blur-xl bg-white/90 border-b border-black/5 shadow-sm" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <a href="#" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={COMPANY_LOGO} 
                alt="SKT Logo" 
                className="h-12 w-12 object-contain"
              />
              <img 
                src={COMPANY_LOGO_FULL} 
                alt="SKT Logistics" 
                className="h-14 object-contain hidden sm:block"
              />
            </a>
            <a
              href="/skt-admin-portal"
              className="w-8 h-8 flex items-center justify-center bg-[#E63946] hover:bg-[#c92a37] rounded-md shadow-sm transition-colors"
              title="Admin Login"
              aria-label="Admin Login"
              data-testid="header-admin-login-btn"
            >
              <Lock className="w-4 h-4 text-white" />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-brand-red ${
                  isScrolled ? "text-slate-700" : "text-white/90"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, "#contact")}
              className="btn-primary"
              data-testid="nav-cta"
            >
              Get Quote
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? "text-navy" : "text-white"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? "text-navy" : "text-white"}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="py-3 px-4 text-slate-700 font-medium hover:bg-slate-50 rounded-lg"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, "#contact")}
                className="btn-primary mt-2 text-center"
              >
                Get Quote
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const [activeBg, setActiveBg] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveBg((prev) => (prev + 1) % heroBackgrounds.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Rotating background images */}
      {heroBackgrounds.map((bg, idx) => (
        <motion.div
          key={idx}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bg.url})` }}
          initial={false}
          animate={{ opacity: activeBg === idx ? 1 : 0, scale: activeBg === idx ? 1 : 1.05 }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
        />
      ))}
      <div className="absolute inset-0 bg-navy/70" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="inline-block mb-6 text-brand-red font-semibold text-sm uppercase tracking-[0.2em]">
            Trusted Since 1983
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-tight mb-4">
            35+ Years of<br />
            <span className="text-brand-red">Logistics Excellence</span>
          </h1>
          <p className="text-xl md:text-2xl text-white font-semibold italic mb-6 tracking-wide">
            "Connecting Routes. Delivering Results."
          </p>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
            India's trusted partner for Road Transport, Air Freight, and Container Logistics. 
            Delivering reliability across the nation with precision and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#services"
              className="btn-primary text-lg px-8 py-4"
              data-testid="hero-cta-services"
            >
              Explore Our Services
              <ChevronRight className="w-5 h-5 ml-2 inline" />
            </a>
            <a
              href="#contact"
              className="btn-secondary text-lg px-8 py-4"
              data-testid="hero-cta-contact"
            >
              Request a Quote
            </a>
          </div>
        </motion.div>

        {/* Stats Strip */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
        >
          {[
            { value: "35+", label: "Years Experience" },
            { value: "25+", label: "Self Owned Fleet" },
            { value: "150M+", label: "INR Turnover" },
            { value: "7", label: "Branch Offices" },
          ].map((stat, idx) => (
            <div key={idx} className="stat-card" data-testid={`hero-stat-${idx}`}>
              <span className="text-3xl md:text-4xl font-black text-brand-red">{stat.value}</span>
              <span className="text-white/70 text-sm uppercase tracking-wide">{stat.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Background carousel indicator dots */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {heroBackgrounds.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveBg(idx)}
            className={`h-1.5 rounded-full transition-all ${
              activeBg === idx ? 'w-8 bg-brand-red' : 'w-3 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Show background ${idx + 1}`}
            data-testid={`hero-bg-dot-${idx}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-3 bg-white/50 rounded-full mt-2"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Company Overview Section
const OverviewSection = () => {
  return (
    <AnimatedSection className="py-24 bg-slate-50" id="overview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label">About Us</span>
          <h2 className="section-title">Company Overview</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeInUp}>
            <h3 className="text-2xl md:text-3xl font-bold text-navy mb-6">
              SKT Logistics Private Limited
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Established with a vision to transform India's logistics landscape, SKT Logistics has 
              been a cornerstone of excellence for over three decades. With our headquarters in 
              Bengaluru and a strategic network spanning key industrial hubs, we have built an 
              unparalleled reputation for delivering goods safely, swiftly, and efficiently.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Under the visionary leadership of our Promoter Directors, <strong>B Venkataramana</strong> and 
              <strong> Deepashree Venkataramana</strong>, we have grown from a regional carrier to a 
              national logistics powerhouse, serving Fortune 500 companies and leading Indian enterprises.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-navy font-medium">
                <Building2 className="w-5 h-5 text-brand-red" />
                <span>Established 1983</span>
              </div>
              <div className="flex items-center gap-2 text-navy font-medium">
                <Users className="w-5 h-5 text-brand-red" />
                <span>Family-Led Enterprise</span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
            {[
              { icon: <Truck />, title: "25+ Fleet", desc: "Self-owned vehicles" },
              { icon: <Globe />, title: "7 Branches", desc: "Pan-India presence" },
              { icon: <Award />, title: "150M+ INR", desc: "Annual turnover" },
              { icon: <Shield />, title: "35+ Years", desc: "Industry expertise" },
            ].map((item, idx) => (
              <div key={idx} className="info-card" data-testid={`overview-card-${idx}`}>
                <div className="w-12 h-12 bg-brand-red/10 rounded-lg flex items-center justify-center text-brand-red mb-4">
                  {item.icon}
                </div>
                <h4 className="font-bold text-navy text-lg">{item.title}</h4>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Vision & Mission Section
const VisionMissionSection = () => {
  return (
    <AnimatedSection className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div variants={fadeInUp} className="vision-card" data-testid="vision-card">
            <div className="w-14 h-14 bg-navy rounded-xl flex items-center justify-center mb-6">
              <Eye className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-navy mb-4">Our Vision</h3>
            <p className="text-slate-600 leading-relaxed">
              To be India's most trusted and innovative logistics partner, setting industry 
              benchmarks in operational excellence, customer satisfaction, and sustainable practices 
              while expanding our footprint across global markets.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="mission-card" data-testid="mission-card">
            <div className="w-14 h-14 bg-brand-red rounded-xl flex items-center justify-center mb-6">
              <Target className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-navy mb-4">Our Mission</h3>
            <p className="text-slate-600 leading-relaxed">
              To deliver seamless, reliable, and cost-effective logistics solutions through 
              continuous innovation, strategic infrastructure investments, and an unwavering 
              commitment to exceeding customer expectations in every shipment, every time.
            </p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Core Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Truck className="w-10 h-10" />,
      title: "Road Transport",
      description: "Comprehensive roadway logistics with our fleet of 25+ self-owned vehicles covering major industrial corridors across India. FTL and LTL solutions available.",
      features: ["Full Truckload (FTL)", "Less Than Truckload (LTL)", "Express Delivery", "Dedicated Fleet"]
    },
    {
      icon: <Plane className="w-10 h-10" />,
      title: "Air Freight",
      description: "Time-critical shipments delivered through strategic partnerships with leading airlines. Domestic and international air cargo solutions.",
      features: ["Domestic Air Cargo", "International Freight", "Airport-to-Door", "Priority Handling"]
    },
    {
      icon: <Package className="w-10 h-10" />,
      title: "Container Logistics",
      description: "End-to-end container management from port to destination. FCL and LCL services with customs clearance support.",
      features: ["FCL & LCL Services", "Port Operations", "Customs Clearance", "Multimodal Transport"]
    },
  ];

  return (
    <AnimatedSection className="py-24 bg-slate-50" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label">What We Offer</span>
          <h2 className="section-title">Core Services</h2>
          <p className="text-slate-600 max-w-2xl mx-auto mt-4">
            Comprehensive logistics solutions tailored to meet the diverse needs of modern businesses
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="service-card group"
              data-testid={`service-card-${idx}`}
            >
              <div className="w-16 h-16 bg-brand-red/10 rounded-xl flex items-center justify-center text-brand-red mb-6 group-hover:bg-brand-red group-hover:text-white transition-all duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-navy mb-3">{service.title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, fidx) => (
                  <li key={fidx} className="flex items-center gap-2 text-slate-500 text-sm">
                    <ChevronRight className="w-4 h-4 text-brand-red" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Infrastructure & Network Section
const InfrastructureSection = () => {
  const branchList = [
    "Bengaluru (Headquarters)",
    "Chennai",
    "Pune", 
    "Coimbatore",
    "Belgaum",
    "Pondicherry",
    "Shimoga"
  ];

  return (
    <section
      id="network"
      data-testid="infrastructure-section"
      className="relative py-24 bg-slate-100 overflow-hidden"
    >
      {/* Light background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url(${INFRASTRUCTURE_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%)"
        }} />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="section-label">Our Reach</span>
            <h2 className="section-title text-navy">Infrastructure & Network Strength</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <h3 className="text-2xl font-bold text-navy mb-6">
                Strategic Pan-India Presence
              </h3>
              <p className="text-slate-700 leading-relaxed mb-8">
                Our strategically located branches ensure seamless connectivity across South and West India's 
                major industrial hubs. Each facility is equipped with modern warehousing capabilities and 
                staffed by experienced logistics professionals.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {branchList.map((branch, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 text-navy"
                    data-testid={`branch-${idx}`}
                  >
                    <MapPin className="w-5 h-5 text-brand-red flex-shrink-0" />
                    <span className="text-sm font-medium">{branch}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-6">
              {[
                { value: "25+", label: "Self-Owned Vehicles" },
                { value: "7", label: "Branch Offices" },
                { value: "24/7", label: "Operations" },
                { value: "1000+", label: "Deliveries Monthly" },
              ].map((stat, idx) => (
                <div key={idx} className="infra-stat-card-light" data-testid={`infra-stat-${idx}`}>
                  <span className="text-3xl font-black text-brand-red">{stat.value}</span>
                  <span className="text-slate-600 text-sm font-medium">{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Key Differentiators Section
const DifferentiatorsSection = () => {
  const differentiators = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Safety First",
      description: "Stringent safety protocols and comprehensive insurance coverage for every shipment."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "On-Time Delivery",
      description: "Industry-leading on-time delivery rates backed by robust route optimization."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Reliability",
      description: "Three decades of consistent service excellence trusted by Fortune 500 companies."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Tracking",
      description: "Advanced GPS tracking and monitoring systems for complete shipment visibility."
    },
  ];

  return (
    <AnimatedSection className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label">Why Choose Us</span>
          <h2 className="section-title">Key Differentiators</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentiators.map((item, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="differentiator-card"
              data-testid={`differentiator-${idx}`}
            >
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center text-brand-red mb-5 group-hover:bg-brand-red group-hover:text-white transition-all">
                {item.icon}
              </div>
              <h4 className="font-bold text-navy text-lg mb-2">{item.title}</h4>
              <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Clientele Section with Logos
const ClienteleSection = () => {
  return (
    <AnimatedSection className="py-24 bg-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label text-brand-red">Our Partners</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Trusted By Industry Leaders</h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            We are proud to partner with some of India's most respected companies, delivering logistics excellence across diverse industries.
          </p>
        </motion.div>

        <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {clients.map((client, idx) => (
            <div
              key={idx}
              className="client-card group"
              data-testid={`client-${idx}`}
            >
              {client.logoUrl ? (
                <div className="h-20 mb-4 mx-auto bg-white rounded-lg flex items-center justify-center p-3 transition-transform group-hover:scale-105 overflow-hidden">
                  <img
                    src={client.logoUrl}
                    alt={`${client.name} logo`}
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  className="h-20 px-6 py-4 rounded-lg mb-4 mx-auto flex items-center justify-center transition-transform group-hover:scale-105"
                  style={{ backgroundColor: client.bgColor }}
                >
                  <span
                    className="font-black text-lg tracking-tight"
                    style={{ color: client.textColor }}
                  >
                    {client.displayName}
                  </span>
                </div>
              )}
              <p className="text-white/80 text-sm text-center font-medium group-hover:text-white transition-colors">
                {client.name}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Scrolling marquee */}
        <motion.div variants={fadeInUp} className="mt-12 pt-12 border-t border-white/10">
          <Marquee
            gradient={false}
            speed={40}
            className="py-4"
          >
            {clients.concat(clients).map((client, idx) => (
              <div key={idx} className="flex items-center mx-8">
                {client.logoUrl ? (
                  <div className="h-12 w-32 bg-white rounded flex items-center justify-center p-2">
                    <img
                      src={client.logoUrl}
                      alt={`${client.name} logo`}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    className="h-12 px-4 rounded flex items-center justify-center"
                    style={{ backgroundColor: client.bgColor }}
                  >
                    <span
                      className="font-bold text-sm"
                      style={{ color: client.textColor }}
                    >
                      {client.displayName}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </Marquee>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Fleet Gallery Section
const GallerySection = () => {
  return (
    <AnimatedSection className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label">Our Assets</span>
          <h2 className="section-title">Fleet & Operations Gallery</h2>
          <p className="text-slate-600 max-w-2xl mx-auto mt-4">
            A glimpse into our extensive logistics infrastructure and operational capabilities
          </p>
        </motion.div>

        <motion.div 
          variants={fadeInUp}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] md:auto-rows-[200px]"
          data-testid="fleet-gallery"
        >
          {galleryImages.map((img, idx) => (
            <div
              key={idx}
              className={`gallery-item group ${img.span}`}
              data-testid={`gallery-item-${idx}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="gallery-overlay">
                <div className="flex flex-col items-start">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-red mb-1">{img.category}</span>
                  <span className="text-white font-bold text-lg">{img.alt}</span>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Stats below gallery */}
        <motion.div variants={fadeInUp} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "25+", label: "Self-Owned Fleet" },
            { value: "7", label: "Warehouses" },
            { value: "24/7", label: "Operations" },
            { value: "100%", label: "GPS Tracked" },
          ].map((stat, idx) => (
            <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-2xl font-black text-brand-red">{stat.value}</span>
              <p className="text-slate-600 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Future Growth Section
const FutureGrowthSection = () => {
  return (
    <AnimatedSection className="py-24 bg-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeInUp}>
            <span className="section-label text-brand-red">Looking Ahead</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Future Growth & Expansion Plans
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              As we enter our fourth decade of operations, SKT Logistics is poised for significant 
              expansion. Our strategic roadmap includes technology upgrades, fleet modernization, 
              and network expansion to serve emerging industrial corridors.
            </p>
            <ul className="space-y-4">
              {[
                "Expanding to 15+ branch locations by 2027",
                "Fleet modernization with eco-friendly vehicles",
                "Advanced AI-powered logistics optimization",
                "International freight partnerships"
              ].map((plan, idx) => (
                <li key={idx} className="flex items-start gap-3 text-white/80">
                  <ChevronRight className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                  <span>{plan}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={fadeInUp} className="relative">
            <div className="absolute inset-0 bg-brand-red/20 rounded-3xl transform rotate-3" />
            <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">Our Commitment</h3>
              <p className="text-white/70 leading-relaxed">
                We remain committed to delivering exceptional value through innovation, 
                sustainable practices, and an unwavering focus on customer success. 
                Our journey of 35+ years is just the beginning.
              </p>
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex items-center gap-4">
                  <img 
                    src={COMPANY_LOGO} 
                    alt="SKT Logo" 
                    className="w-16 h-16 object-contain bg-white rounded-xl p-1"
                  />
                  <img
                    src={COMPANY_LOGO_FULL}
                    alt="SKT Logistics"
                    className="h-12 object-contain bg-white rounded-xl p-1"
                  />
                </div>
                <p className="text-white/60 text-sm mt-4">Excellence in Every Mile</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", inquiry: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      setSubmitStatus("success"); // Show success for demo purposes
      setFormData({ name: "", email: "", phone: "", inquiry: "" });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <AnimatedSection className="py-24 bg-slate-50" id="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">Contact Us</h2>
          <p className="text-slate-600 max-w-xl mx-auto mt-4">
            Ready to streamline your logistics? Reach out to us for a customized quote.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div variants={fadeInUp}>
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Full Name</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    className="form-input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Your Inquiry</label>
                  <textarea
                    required
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Tell us about your logistics requirements..."
                    value={formData.inquiry}
                    onChange={(e) => setFormData({...formData, inquiry: e.target.value})}
                    data-testid="contact-inquiry-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full py-4 disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? "Sending..." : "Send Inquiry"}
                </button>

                {submitStatus === "success" && (
                  <div className="p-4 bg-green-50 text-green-700 rounded-lg text-center" data-testid="contact-success-message">
                    Thank you! We'll get back to you shortly.
                  </div>
                )}
              </div>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={fadeInUp} className="space-y-8">
            <div className="contact-info-card" data-testid="contact-info">
              <h3 className="text-xl font-bold text-navy mb-6">Corporate Headquarters</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-brand-red flex-shrink-0" />
                  <div>
                    <p className="font-medium text-navy">SKT Logistics Private Limited</p>
                    <p className="text-slate-600">Bengaluru, Karnataka, India</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-6 h-6 text-brand-red flex-shrink-0" />
                  <div>
                    <a href="tel:+919845038820" className="text-slate-600 hover:text-brand-red font-medium">+91-9845038820</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-6 h-6 text-brand-red flex-shrink-0" />
                  <div>
                    <p className="text-slate-600">info@sktlogistics.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-navy rounded-2xl p-8">
              <h4 className="text-lg font-bold text-white mb-4">Business Hours</h4>
              <div className="space-y-2 text-white/70">
                <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                <p>Sunday: Closed</p>
                <p className="pt-4 text-brand-red font-medium">24/7 Operations Support Available</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-navy py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-4">
              <img src={COMPANY_LOGO} alt="SKT Logo" className="h-14 w-14 object-contain" />
              <img src={COMPANY_LOGO_FULL} alt="SKT Logistics" className="h-16 object-contain" />
            </div>
            <p className="text-white/60 leading-relaxed max-w-md">
              India's trusted logistics partner for over 35 years. Delivering excellence 
              in road transport, air freight, and container logistics across the nation.
            </p>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {["Overview", "Services", "Network", "Contact"].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase()}`} 
                    className="text-white/60 hover:text-brand-red transition-colors"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Our Services</h4>
            <ul className="space-y-3">
              {["Road Transport", "Air Freight", "Container Logistics", "Express Delivery"].map((service) => (
                <li key={service} className="text-white/60">{service}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} SKT Logistics Private Limited. All rights reserved.
          </p>
          <p className="text-white/40 text-sm">
            Promoter Directors: B Venkataramana & Deepashree Venkataramana
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Header />
      <main>
        <HeroSection />
        <OverviewSection />
        <VisionMissionSection />
        <ServicesSection />
        <TrackingSection />
        <InfrastructureSection />
        <DifferentiatorsSection />
        <ClienteleSection />
        <GallerySection />
        <MapSection />
        <FutureGrowthSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
